import { CommonModule, DatePipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';

// Modular D3 imports for performance
import { select, pointer, Selection } from 'd3-selection';
import { scaleLinear, scaleLog, scaleBand, ScaleBand, ScaleLinear, ScaleLogarithmic } from 'd3-scale';
import { max } from 'd3-array';
import { axisBottom, axisLeft } from 'd3-axis';
import { line, curveMonotoneX } from 'd3-shape';
import 'd3-transition'; // Required for .transition() method

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isoWeek from 'dayjs/plugin/isoWeek';
import {
  ThemeService,
  Theme,
} from '../../../../../core/services/theme.service';
import { Subscription } from 'rxjs';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);

type colorLegend = `#${string}`;

// --- Added Interfaces for better Type Safety ---
export interface BarChartData {
  start: string;
  end: string;
  month_short?: string;
  [key: string]: any; // For dynamic keys like 'total', 'dental', etc.
}

export interface LegendData {
  label: string;
  color: colorLegend;
}

export interface ColorMap {
  data1: colorLegend | '';
  data2: colorLegend | '';
  data3: colorLegend | '';
  data4: colorLegend | '';
}

export interface TooltipConfig {
  label: string;
  key: string;
  type?: 'percent' | string;
}
// ---

@Component({
  selector: 'app-multi-bar-grid-chart',
  templateUrl: './multi-bar-grid-chart.html',
  styles: [`
    .chart-container-class {
      /* Custom scrollbar styling for a premium look */
      &::-webkit-scrollbar {
        height: 6px;
      }
      &::-webkit-scrollbar-track {
        background: transparent;
      }
      &::-webkit-scrollbar-thumb {
        background: rgba(203, 213, 225, 0.5); /* transparent slate-300 */
        border-radius: 3px;
        transition: background 0.2s ease;
        &:hover {
          background: rgba(148, 143, 184, 0.8);
        }
      }

      /* For dark mode */
      :host-context(.dark) &::-webkit-scrollbar-thumb {
        background: rgba(71, 85, 105, 0.5); /* transparent slate-600 */
        &:hover {
          background: rgba(100, 116, 139, 0.8);
        }
      }

      scrollbar-width: thin;
      scrollbar-color: rgba(203, 213, 225, 0.5) transparent;
    }
  `],
  providers: [DatePipe],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule],
})
export class MultiBarGridChartComponent
  implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('chartContainer') chartContainer!: ElementRef;

  @Input() data3: BarChartData[] = [];
  @Input() data1: BarChartData[] = [];
  @Input() data2: BarChartData[] = [];
  @Input() data4: BarChartData[] = [];
  @Input() legendData: LegendData[] = [];
  @Input() showYAxis: boolean = true;
  @Input() yAxisShowOnlyFirstLast: boolean = false;
  @Input() showAllYticks: boolean = false;
  @Input() showDynamicxAxisLabel: boolean = false;
  @Input() barClusterPixel: number = 40;
  @Input() barClusterHeight: number = 40;
  @Input() darkBarOutlineFill: string = '';
  @Input() lightBarOutlineFill: string = '';
  @Input() unit: string = '';
  @Input() autoScrollOn5Years: boolean = false;

  // @Input() unit: 'month' | 'quarter' | 'year' = 'month';
  @Input() showTooltipLabel: boolean = true;
  @Input() showXAxisLabels: boolean = true;
  @Input() showYAxisLabels: boolean = true;
  @Input() showValueLabels: boolean = true;

  private readonly margin = { top: 30, right: 0, bottom: 30, left: 40 };
  private resizeObserver?: ResizeObserver;
  private themeSubscription?: Subscription;
  private currentTheme: Theme = 'light';
  private combinedData: (BarChartData & { color: colorLegend })[][] = [];
  fullBarWidth: number = 0;
  gap: number = 2;

  @Input() colors: ColorMap = {
    data1: '',
    data2: '',
    data3: '',
    data4: '',
  };
  @Input() valueKey = 'total';
  @Input() otherKey: string = '';
  @Input() valueType: '$' | '%' = '%';
  @Input() addGradient: boolean = true;
  @Input() barTextposition: string = 'top';
  @Input() barTextColor: colorLegend = '' as colorLegend;
  @Input() tooltipData: TooltipConfig[] = [
    { label: 'Enrolled', key: 'enrolled' },
    { label: 'Enquiries', key: 'enquiries' },
    { label: 'Cost in 20s', key: 'cost_in_20s' },
  ];

  @Input() labelKey: any;
  @Input() flatBars: boolean = false;
  @Input() yScaleType: 'linear' | 'log' = 'linear';

  private themeService = inject(ThemeService);

  constructor(private datePipe: DatePipe) { }

  ngAfterViewInit() {
    // Subscribe to theme changes
    this.currentTheme = this.themeService.getTheme();
    this.themeSubscription = this.themeService.theme$.subscribe((theme) => {
      this.currentTheme = theme;
      this.renderChart(); // Redraw chart with new theme colors
    });

    this.observeResize();
    this.renderChart();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['data1'] ||
      changes['data2'] ||
      changes['data3'] ||
      changes['data4']
    ) {
      this.renderChart();
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }
  }

  // Get theme-aware colors
  private getThemeColors() {
    const isDark = this.currentTheme === 'dark';
    return {
      // Bar colors - using different shades for multiple bars
      primaryBar: this.colors.data1 || (isDark ? '#f472b6' : '#b927ff'), // pink-400 / pink-500
      secondaryBar: this.colors.data2 || (isDark ? '#a78bfa' : '#8b5cf6'), // purple-400 / purple-600
      tertiaryBar: this.colors.data3 || (isDark ? '#818cf8' : '#6366f1'), // indigo-400 / indigo-500
      forthBar: this.colors.data4 || (isDark ? '#81f891ff' : '#ec63f1ff'), // indigo-400 / indigo-500

      // Text colors
      barTextColor: this.barTextColor || (isDark ? '#f4f4f5' : '#000000'), // slate-100 / black
      axisText: isDark ? '#d4d4d8' : '#64748b', // slate-300 / slate-500
      axisLine: isDark ? '#85858e' : '#94a3b8', // slate-500 / slate-400 - More prominent

      // Background gradients
      gradientNormalStart: isDark ? '#1e1e21' : '#E7EFF8', // slate-800 / light blue
      gradientNormalEnd: isDark ? '#151517' : '#FFFFFF', // slate-900 / white
      gradientReversedStart: isDark ? '#151517' : '#FFFFFF', // slate-900 / white
      gradientReversedEnd: isDark ? '#1e1e21' : '#E7EFF8', // slate-800 / light blue

      // Bar background/outline
      barOutlineFill:
        isDark && this.darkBarOutlineFill
          ? this.darkBarOutlineFill
          : this.lightBarOutlineFill
            ? this.lightBarOutlineFill
            : isDark
              ? '#1e293b'
              : '#fff', // slate-800 / white
      // barOutlineFill: isDark ? '#1e1e21' : '#fff', // slate-800 / white
      barOutlineStroke: isDark ? '#47474e' : '#CBD1D9', // slate-600 / gray-300

      // Tooltip colors
      tooltipBg: isDark ? '#232327' : '#ffffff', // slate-800 / white
      tooltipBorder: isDark ? '#47474e' : '#F2F5F8', // slate-600 / light gray
      tooltipText: isDark ? '#AD46FF' : '#7D7D93', // slate-100 / dark
      tooltipSecondaryText: isDark ? '#7D7D93' : '#656B73', // slate-400 / gray-600
      tooltipDivider: isDark ? '#47474e' : '#F2F5F8', // slate-600 / light gray
      tooltipYearColor: isDark ? '#FFFFFF' : '#141437', // slate-600 / light gray

      // Legend colors
      legendText: isDark ? '#e4e4e7' : '#1a1a1a', // slate-200 / dark
    };
  }

  private observeResize() {
    this.resizeObserver = new ResizeObserver(() => this.renderChart());
    this.resizeObserver.observe(this.chartContainer?.nativeElement);
  }

  private clearChart() {
    const container = this.chartContainer?.nativeElement;
    if (container) {
      select(container)
        .style('overflow-x', null)
        .style('overflow-y', null);
    }
    select(this.chartContainer?.nativeElement).selectAll('*').remove();
  }

  /**
   * Main Render Function
   */
  private renderChart(): void {
    // We expect data1, data2, data3 as the primary columns
    if (!this.data1?.length) {
      this.clearChart();
      return;
    }

    this.clearChart();

    const container = this.chartContainer?.nativeElement;
    const parent = container?.parentElement?.parentElement;
    if (!parent) return;

    // 1. Setup Dimensions
    // Theme colors
    const colors = this.getThemeColors();

    const margin = { top: 60, right: 20, bottom: 40, left: 60 }; // Increased margin to prevent clipping long labels
    const height = parent.clientHeight; // Use simpler height logic
    const needsScroll = this.autoScrollOn5Years && this.has5OrMoreYearsOfData();
    let width = parent.clientWidth;
    if (needsScroll) {
      const minWidthPerPoint = 100;
      width = Math.max(width, this.data1.length * minWidthPerPoint + margin.left + margin.right);
    }

    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    // Apply scroll style to container
    select(container)
      .style('overflow-x', needsScroll ? 'auto' : 'visible')
      .style('overflow-y', 'hidden')
      .style('width', '100%');

    const svg = select(container)
      .append('svg')
      .attr('width', needsScroll ? width : '100%')
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .style('max-width', needsScroll ? 'none' : '100%')
      .style('height', 'auto');

    // 2. Prepare Data
    // We assume data1, data2, data3 are aligned by index (same months).
    // We'll map them to a structure: { label: string, values: [val1, val2, val3] }
    const labels = this.data1.map((d) => {
      if (this.unit === 'year') {
        return d.month_short || dayjs(d.start).format('YYYY');
      }
      return d.month_short
        ? `${d.month_short} ${dayjs(d.start).format('YYYY')}`
        : dayjs(d.start).format('MMM YYYY');
    });

    // Combine for Scale max
    const allValues: number[] = [];
    [this.data1, this.data2, this.data3, this.data4].forEach((ds) => {
      if (ds) ds.forEach((d) => allValues.push(Number(d[this.valueKey] || 0)));
    });

    const maxVal = max(allValues) || 0;

    // 3. Scales
    const xScale = scaleBand()
      .domain(labels)
      .range([0, innerWidth])
      .padding(0.25);

    const yScale = this.yScaleType === 'log'
      ? scaleLog()
        .domain([1, Math.pow(10, Math.ceil(Math.log10(Math.max(10, maxVal * 1.1))))])
        .range([innerHeight, 0])
        .base(10)
      : scaleLinear()
        .domain([0, maxVal * 1.1])
        .range([innerHeight, 0]);

    // Subgroup scale for the bars (Enrolled, Enquiries, Cost)
    // We determine how many series are present
    const seriesKeys = ['data1', 'data2', 'data3']; // We prioritize these 3
    if (this.data4?.length) seriesKeys.push('data4');

    // Cap bar width to prevent massive bars when few data points exist
    const maxBarWidth = 50;
    const maxGroupWidth = seriesKeys.length * maxBarWidth;
    const actualGroupWidth = Math.min(xScale.bandwidth(), maxGroupWidth);

    const xSubgroup = scaleBand()
      .domain(seriesKeys)
      .range([0, actualGroupWidth])
      .padding(0.1);

    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // 4. Grid Lines (Horizontal only, behind bars)
    // We use axisLeft with tickSize(-width)
    const yAxisGrid = axisLeft(yScale)
      .tickSize(-innerWidth)
      .tickFormat(() => '') // No labels on grid lines
      .ticks(this.yScaleType === 'log' ? 10 : 5); // More lines for log scale

    g.append('g')
      .attr('class', 'grid')
      .call(yAxisGrid)
      .selectAll('line')
      .attr('stroke', this.yScaleType === 'log' ? 'transparent' : (this.currentTheme === 'dark' ? '#e2e8f00e' : '#e2e8f0ff'))
      .attr('stroke-width', 1);

    g.selectAll('.grid path').remove(); // Remove axis line

    // 4a. Tooltip Container
    const tooltip = select(container)
      .append('div')
      .attr('class', 'tooltip')
      .style('opacity', 0)
      .style('position', 'absolute')
      .style('pointer-events', 'none');

    // 5. Draw Bars & Values
    // We iterate index because datasets are separate arrays
    const groupOffset = (xScale.bandwidth() - actualGroupWidth) / 2;

    this.data1.forEach((d1, i) => {
      const label = labels[i];
      const groupX = (xScale(label) || 0) + groupOffset;

      // Prepare Data Points for this group
      const groupData = [
        {
          key: 'data1',
          value: d1[this.valueKey],
          color: colors.primaryBar,
          original: d1,
          tooltipIndex: 0,
        },
        {
          key: 'data2',
          value: this.data2?.[i]?.[this.valueKey],
          color: colors.secondaryBar,
          original: this.data2?.[i],
          tooltipIndex: 1,
        },
        {
          key: 'data3',
          value: this.data3?.[i]?.[this.valueKey],
          color: colors.tertiaryBar,
          original: this.data3?.[i],
          tooltipIndex: 2,
        },
      ];
      if (this.data4?.length && this.data4[i]) {
        groupData.push({
          key: 'data4',
          value: this.data4[i][this.valueKey],
          color: colors.forthBar,
          original: this.data4[i],
          tooltipIndex: 3,
        });
      }

      const barGroup = g
        .append('g')
        .attr('transform', `translate(${groupX}, 0)`);

      groupData.forEach((d) => {
        if (d.value === undefined) return;
        const barX = xSubgroup(d.key) || 0;
        const barValue = this.yScaleType === 'log' ? Math.max(1, d.value || 0) : (d.value || 0);
        const barY = yScale(barValue);
        const barHeight = Math.max(0, innerHeight - barY);
        const barWidth = xSubgroup.bandwidth();

        // Draw Bar
        const bar = barGroup
          .append('rect')
          .attr('x', barX)
          .attr('y', barY)
          .attr('width', barWidth)
          .attr('height', barHeight)
          .attr('fill', d.color)
          .style('cursor', 'pointer');

        // Interactive effects (Tooltip + Hover)
        bar
          .on('mouseover', (event: MouseEvent) => {
            // Hover effect
            select(event.currentTarget as Element)
              .transition()
              .duration(200)
              .attr('opacity', 0.7);

            // Tooltip Content
            tooltip
              .html(
                this.getToolTipHtml(
                  d.original || d1,
                  this.tooltipData,
                  this.datePipe,
                  colors,
                  d.tooltipIndex // Pass specific index
                )
              )
              .style('opacity', 1);
          })
          .on('mousemove', (event: MouseEvent) => {
            const [ptrX, ptrY] = pointer(event, container);
            const tooltipNode = tooltip.node() as HTMLElement;
            const tooltipWidth = tooltipNode.getBoundingClientRect().width;
            const tooltipHeight = tooltipNode.getBoundingClientRect().height;

            let tooltipLeft = ptrX + 15;
            if (tooltipLeft + tooltipWidth > width) {
              tooltipLeft = ptrX - 15 - tooltipWidth;
            }
            let tooltipTop = ptrY - 5 - tooltipHeight;
            if (tooltipTop < 0) tooltipTop = 0;

            tooltip
              .style('left', `${tooltipLeft}px`)
              .style('top', `${tooltipTop}px`);
          })
          .on('mouseleave', (event: MouseEvent) => {
            select(event.currentTarget as Element)
              .transition()
              .duration(200)
              .attr('opacity', 1);
            tooltip.style('opacity', 0);
          });

        // Draw Value on Top
        if (this.showValueLabels) {
          let valText = d.value.toString();
          if (d.key === 'data3') {
            valText = `$${d.value}`;
          }

          const textGroup = barGroup.append('g').style('cursor', 'pointer');
          const fontSize = 10;
          const padding = 2;

          const textObj = textGroup
            .append('text')
            .attr('x', barX + barWidth / 2)
            .attr('y', Math.min(innerHeight - 5, barY - 5))
            .attr('text-anchor', 'middle')
            .attr('font-size', `${fontSize}px`)
            .attr('fill', '#000')
            .attr('font-weight', '500')
            .text(valText);

          // Add background box for value
          const bbox = textObj.node()?.getBBox();
          if (bbox) {
            textGroup
              .insert('rect', 'text')
              .attr('x', bbox.x - padding)
              .attr('y', bbox.y - padding)
              .attr('width', bbox.width + padding * 2)
              .attr('height', bbox.height + padding * 2)
              .attr('fill', '#E5E7EB')
              .attr('opacity', 0.8)
              .attr('rx', 2);
          }

          // Attach tooltip events to the textGroup (covers both text and background)
          textGroup
            .on('mouseover', (event: MouseEvent) => {
              // Optional: highlight bar associated with this label?
              bar.transition().duration(200).attr('opacity', 0.7);

              tooltip
                .html(
                  this.getToolTipHtml(
                    d.original || d1,
                    this.tooltipData,
                    this.datePipe,
                    colors,
                    d.tooltipIndex
                  )
                )
                .style('opacity', 1);
            })
            .on('mousemove', (event: MouseEvent) => {
              const [ptrX, ptrY] = pointer(event, container);
              const tooltipNode = tooltip.node() as HTMLElement;
              const tooltipWidth = tooltipNode.getBoundingClientRect().width;
              const tooltipHeight = tooltipNode.getBoundingClientRect().height;

              let tooltipLeft = ptrX + 15;
              if (tooltipLeft + tooltipWidth > width) {
                tooltipLeft = ptrX - 15 - tooltipWidth;
              }
              let tooltipTop = ptrY - 5 - tooltipHeight;
              if (tooltipTop < 0) tooltipTop = 0;

              tooltip
                .style('left', `${tooltipLeft}px`)
                .style('top', `${tooltipTop}px`);
            })
            .on('mouseleave', (event: MouseEvent) => {
              bar.transition().duration(200).attr('opacity', 1);
              tooltip.style('opacity', 0);
            });
        }
      });
    });

    // 6. Axis
    // X Axis
    const xAxis = axisBottom(xScale).tickSize(0).tickPadding(10);
    g.append('g')
      .attr('transform', `translate(0, ${innerHeight})`)
      .call(xAxis)
      .selectAll('text')
      .style('font-size', '12px')
      .attr('color', colors.axisText)
      .style('display', this.showXAxisLabels ? 'block' : 'none');

    g.select('.domain').remove(); // Remove X axis line if desired (image has a line)
    // Image has a black line at bottom
    g.append('line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', innerHeight)
      .attr('y2', innerHeight)
      .attr('stroke', '#00000027')
      .attr('stroke-width', 1);

    // Y Axis (Labels only, grid handled separately)
    const yAxis = axisLeft(yScale)
      .tickSize(0)
      .tickPadding(10)
      .ticks(this.yScaleType === 'log' ? 5 : 5)
      .tickFormat((d: any) => {
        const val = Number(d);
        if (this.yScaleType === 'log') {
          // Only show exact powers of 10
          const log = Math.log10(val);
          if (Math.abs(log - Math.round(log)) > 1e-10) return '';
        }
        //if (val >= 1000000) return val / 1000000 + 'M';
        if (val >= 1000) return val / 1000 + 'K';
        return val.toString();
      });

    g.append('g')
      .call(yAxis)
      .selectAll('text')
      .style('font-size', '11px')
      .attr('color', colors.axisText)
      .style('display', this.showYAxisLabels ? 'block' : 'none');

    g.select('.domain').remove(); // Remove Y axis vertical line

    // 7. Legend (Top Left/Center)
    // Image shows: [Rect Green] Enrolled   [Rect Cyan] Enquiries   [Rect Blue] Cost in 20s
    // aligned to top left.
    if (this.legendData.length) {
      const legendG = svg
        .append('g')
        .attr('transform', `translate(${margin.left}, 20)`);

      let xOff = 0;
      this.legendData.forEach((l, i) => {
        // Rect
        legendG
          .append('rect')
          .attr('x', xOff)
          .attr('y', 0)
          .attr('width', 30)
          .attr('height', 12)
          .attr('fill', l.color)
          .attr('rx', 2);

        // Text
        const text = legendG
          .append('text')
          .attr('x', xOff + 35)
          .attr('y', 10)
          .text(l.label) // l.year holds the label name here
          .attr('font-size', '12px')
          .attr('font-weight', '500')
          .attr('fill', colors.legendText);

        const bbox = text.node()?.getBBox();
        xOff += 35 + (bbox?.width || 50) + 20; // Spacing
      });
    }
  }

  /**
   * Option C (extended):
   *  - data1, data2, data3 → MAIN datasets (mandatory)
   *  - data4, data5, data6... → OPTIONAL datasets
   *
   *  All datasets align to data1.labels
   */
  private combineDataForOptionC(
    colors: ReturnType<typeof this.getThemeColors>
  ) {
    // Collect datasets dynamically
    const datasets: any[][] = [];

    // Push first 3 main datasets
    if (Array.isArray(this.data1)) datasets.push(this.data1);
    if (Array.isArray(this.data2)) datasets.push(this.data2);
    if (Array.isArray(this.data3)) datasets.push(this.data3);

    // Push any additional optional datasets
    const optionalSets = [this.data4];

    optionalSets.forEach((set) => {
      if (Array.isArray(set) && set.length > 0) {
        datasets.push(set);
      }
    });

    // Build a color palette big enough for unlimited datasets
    const palette = [
      colors.primaryBar,
      colors.secondaryBar,
      colors.tertiaryBar,
      '#FF7AA2',
      '#9B5CF6',
      '#14D18E',
      '#FFD76B',
      '#3DD9C0',
      '#F48FB1',
      '#7986CB',
    ];

    // Map all datasets by label
    const maps = datasets.map((set) => {
      const m = new Map<string, any>();
      set.forEach((item) => {
        if (item?.label) m.set(String(item.label), item);
      });
      return m;
    });

    const mainLabels = this.data1.map((d) => d?.['label']);

    // Build final cluster structure
    this.combinedData = mainLabels.map((label, index) => {
      const cluster: any[] = [];

      datasets.forEach((set, setIndex) => {
        let found;
        if (setIndex === 0) {
          found = maps[setIndex].get(label) || set[index];
        } else {
          // For data2, data3 etc, assume they are already sorted/aligned by index
          // This prevents "Ghost Matches" on placeholders
          found = set[index];
        }

        if (found) {
          cluster.push({
            ...found,
            // Don't override the label - use the original label from the data
            // Overriding causes values from different dates to be shown together
            color: palette[setIndex % palette.length],
          });
        }
      });

      return cluster;
    });
  }

  private appendDefs(
    svg: Selection<SVGSVGElement, unknown, null, undefined>,
    colors: ReturnType<typeof this.getThemeColors>
  ) {
    const defs = svg.append('defs');

    const createGradient = (id: string, stops: [string, string][]) => {
      const gradient = defs
        .append('linearGradient')
        .attr('id', id)
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', '0%')
        .attr('y2', '100%');

      stops.forEach(([offset, color]) => {
        gradient
          .append('stop')
          .attr('offset', offset)
          .attr('stop-color', color);
      });
    };

    createGradient('gradient-normal', [
      ['0%', colors.gradientNormalStart],
      ['100%', colors.gradientNormalEnd],
    ]);
    createGradient('gradient-reversed', [
      ['0%', colors.gradientReversedStart],
      ['100%', colors.gradientReversedEnd],
    ]);
  }

  private combineData(colors: ReturnType<typeof this.getThemeColors>) {
    const maxLength = Math.max(
      this.data1.length,
      this.data2.length,
      this.data3.length,
      this.data4.length
    );
    this.combinedData = [];

    for (let i = 0; i < maxLength; i++) {
      let data: (BarChartData & { color: colorLegend })[] = [];

      if (this.data1.length && this.data1[i]) {
        // Added check for data existence
        data.push({
          ...this.data1[i],
          color: colors.primaryBar as colorLegend,
        });
      }
      if (this.data2.length && this.data2[i]) {
        data.push({
          ...this.data2[i],
          color: colors.secondaryBar as colorLegend,
        });
      }
      if (this.data3.length && this.data3[i]) {
        data.push({
          ...this.data3[i],
          color: colors.tertiaryBar as colorLegend,
        });
      }
      if (this.data4.length && this.data4[i]) {
        data.push({ ...this.data4[i], color: colors.forthBar as colorLegend });
      }

      this.combinedData.push(data);
    }
  }

  private drawAxes(
    svg: Selection<SVGSVGElement, unknown, null, undefined>,

    x: ScaleBand<string>,
    y: ScaleLinear<number, number>,
    height: number,
    colors: ReturnType<typeof this.getThemeColors>
  ) {
    const tickLabels = this.combinedData.map((item: any) =>
      Array.isArray(item)
        ? item[0].month_short
          ? item[0].month_short
          : this.labelKey == 'week'
            ? this.getWeeksBetween(item[0].start, item[0].end)
            : dayjs(item[0].start).format('MMM YYYY')
        : typeof item == 'object'
          ? item.month_short
            ? item.month_short
            : this.labelKey == 'week'
              ? this.getWeeksBetween(item.start, item.end)
              : dayjs(item.start).format('MMM YYYY')
          : ''
    );

    if (!this.showDynamicxAxisLabel) {
      const xAxisGroup = svg
        .append('g')
        .attr('transform', `translate(15,${height - 2 * this.margin.bottom})`)
        .call(
          axisBottom(x)
            .tickSize(0)
            .tickValues(x.domain())
            .tickFormat((d, i) => tickLabels[i] ?? '')
        );

      xAxisGroup.select('path.domain').remove();

      xAxisGroup
        .selectAll('g.tick text')
        .attr('transform', 'translate(0, 10)')
        .attr('fill', colors.axisText)
        .style('font-size', '14px')
        .style('font-weight', 500);

      let [x0, x1] = this.getOffsetValues(svg);

      xAxisGroup
        .append('line')
        .attr('x1', x0)
        .attr('x2', x1)
        .attr('y1', 0)
        .attr('y2', 0)
        .attr('stroke', colors.axisLine)
        .attr('stroke-width', 2);
    }

    if (this.showDynamicxAxisLabel) {
      const xAxisGroup = svg
        .append('g')
        .attr('transform', `translate(15,${height - 2 * this.margin.bottom})`)
        .call(
          axisBottom(x)
            .tickSize(0)
            .tickValues(x.domain())
            .tickFormat((label) => label)
        );

      xAxisGroup.select('path.domain').remove();

      xAxisGroup
        .selectAll('g.tick text')
        .attr('transform', 'translate(0, 10)')
        .attr('fill', colors.axisText)
        .style('font-size', '14px')
        .style('font-weight', 500);

      let [x0, x1] = this.getOffsetValues(svg);

      xAxisGroup
        .append('line')
        .attr('x1', x0)
        .attr('x2', x1)
        .attr('y1', 0)
        .attr('y2', 0)
        .attr('stroke', colors.axisLine)
        .attr('stroke-width', 2);
    }

    if (this.showYAxis) {
      const yAxis = axisLeft(y).tickSizeOuter(0);

      if (this.yAxisShowOnlyFirstLast) {
        const yTicks = y.ticks();
        if (this.showAllYticks) {
          yAxis.tickValues(yTicks);
        } else if (yTicks && yTicks.length > 1) {
          yAxis.tickValues([yTicks[0], yTicks[yTicks.length - 1]]);
        }
      }

      const yAxisGroup = svg
        .append('g')
        .attr(
          'transform',
          `translate(${this.margin.left - 0}, ${height - 2 * this.margin.bottom - y(0)
          })`
        )
        .call(yAxis);

      yAxisGroup
        .selectAll('text')
        .attr('fill', colors.axisText)
        .style('font-size', '12px')
        .style('font-family', 'Inter, sans-serif');

      yAxisGroup
        .selectAll('line')
        .attr('stroke', colors.axisLine)
        .attr('stroke-width', 0.6);

      yAxisGroup.selectAll('path.domain').remove();

      const [yStart, yEnd] = y.range();
      yAxisGroup
        .append('line')
        .attr('x1', 0)
        .attr('x2', 0)
        .attr('y1', yStart)
        .attr('y2', yEnd)
        .attr('stroke', colors.axisLine || '#000')
        .attr('stroke-width', 2)
        .attr('shape-rendering', 'crispEdges');
    }
  }

  private drawRoundedBar(
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
    roundTopLeft = false,
    roundTopRight = false
  ): string {
    const right = x + width;
    const bottom = y + height;

    return [
      `M${x},${bottom}`,
      `V${y + (roundTopLeft ? radius : 0)}`,
      roundTopLeft
        ? `a${radius},${radius} 0 0 1 ${radius},-${radius}`
        : `V${y}`,
      `H${right - (roundTopRight ? radius : 0)}`,
      roundTopRight
        ? `a${radius},${radius} 0 0 1 ${radius},${radius}`
        : `H${right}`,
      `V${bottom}`,
      `H${x}`,
      `Z`,
    ].join(' ');
  }

  appendLegends(
    svg: Selection<SVGSVGElement, unknown, null, undefined>,
    height: number,
    colors: ReturnType<typeof this.getThemeColors>
  ) {
    if (!this.legendData) {
      return;
    }

    let [x0, x1] = this.getOffsetValues(svg);

    const legend = svg
      .insert('g')
      .attr('class', 'legend')
      .attr('transform', `translate(${x0 || 0}, ${height - 15})`);

    const legendItem = legend
      .selectAll('.legend-item')
      .data(this.legendData)
      .enter()
      .append('g')
      .attr('class', 'legend-item')
      .attr('transform', (d, i) => `translate(${i * 70}, 0)`);

    legendItem
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', 12)
      .attr('height', 12)
      .attr('rx', 2)
      .attr('ry', 2)
      .attr('fill', (_d, i) => {
        if (i === 0) return colors.primaryBar;
        if (i === 1) return colors.secondaryBar;
        return colors.tertiaryBar;
      });

    legendItem
      .append('text')
      .attr('x', 18)
      .attr('y', 10)
      .text((d) => d.label)
      .style('font-size', '12px')
      .style('fill', colors.legendText);
  }

  getOffsetValues(svg: any) {
    const bars = svg.selectAll('.bar-cluster');
    let x0 = 0,
      x1 = 0;
    if (!bars.empty()) {
      const firstBar = bars.nodes()[0];
      const lastBar = bars.nodes()[bars.size() - 1];

      x0 = parseFloat(select(firstBar).select('rect').attr('x') || '0');
      x1 =
        parseFloat(select(lastBar).select('rect').attr('x') || '0') +
        parseFloat(select(lastBar).select('rect').attr('width') || '0');
    }

    return [x0, x1];
  }

  getToolTipHtml(
    bar: any,
    tooltipData: any,
    datePipe: DatePipe,
    colors: ReturnType<typeof this.getThemeColors>,
    specificIndex?: number
  ) {
    // const monthYear =  datePipe.transform(bar.start, 'MMM yyyy') ? datePipe.transform(bar.start, 'dd MMM yyyy') : bar?.label; // Dec 2025
    const monthYear = bar?.label
      ? bar?.label
      : datePipe.transform(bar.start, 'MMM yyyy'); // Dec 2025
    // let monthYear:any = datePipe.transform(bar.start, 'dd MMM yyyy') || '';

    // if(this.unit == 'day'){
    // monthYear = datePipe.transform(bar.start, 'dd MMM yyyy')
    // }
    // if(this.unit == 'week'){
    //   monthYear = bar.label
    // }
    const rows = this.getTooltipRows(bar);

    let html = `
    <div style="
      background:${colors.tooltipBg};
      border:1px solid ${colors.tooltipBorder};
      padding:12px 16px;
      border-radius:12px;
      width:100%;
      box-shadow:0px 4px 14px rgba(0,0,0,0.3);
    ">
      <div style="
        color:${colors.tooltipYearColor};
        font-weight:500;
        margin-bottom:8px;
        font-size:15px;
        // border-bottom:1px solid ${colors.tooltipDivider};
        padding-bottom:6px;
      ">
        ${monthYear}
      </div>
  `;

    tooltipData.forEach((item: any, index: number) => {
      // If specificIndex is provided, skip if it doesn't match
      if (specificIndex !== undefined && index !== specificIndex) return;

      const value = bar[item.key];
      const color = this.legendData[index].color;

      // Normalize text for fuzzy comparison - return empty string if undefined
      const clean = (str: string) =>
        str?.toString().trim().replace(/[:]/g, '').toLowerCase() ?? '';

      const barKeyClean = clean(bar.key);
      const itemLabelClean = clean(item.label);

      const shouldShow =
        specificIndex !== undefined ||
        (value !== undefined && value !== null) ||
        (barKeyClean &&
          itemLabelClean &&
          barKeyClean.includes(itemLabelClean)) || // fuzzy match
        (barKeyClean && itemLabelClean && itemLabelClean.includes(barKeyClean));

      if (shouldShow) {
        let displayValue =
          value !== undefined && value !== null ? value : bar.value ?? '-';
        // Check for cost key or label to add dollar sign
        if (
          item.key === 'cost_in_20s' ||
          item.label.toLowerCase().includes('cost')
        ) {
          displayValue = `$${displayValue}`;
        }

        html += `
      <div class="text-sm pt-1 flex items-center justify-between">
        <span class="pr-2" style="color: ${colors.tooltipSecondaryText};">
          ${this.showTooltipLabel ? item?.label : bar.key}
        </span>
        <span class="font-normal text-right" style="color: ${color};">
          ${displayValue}
        </span>
      </div>
    `;
      }
    });

    html += `</div>`;
    return html;
  }

  //   getLabelForBar(barKey: string, metric: string) {
  //   const isPrev = barKey.toLowerCase().includes("previous");

  //   const labels = {
  //     contractCreated: isPrev ? "Previous Contract Created:" : "Contract Created:",
  //     registeredBusinesses: isPrev ? "Previous Registered Businesses:" : "Registered Businesses:",
  //   };

  //   return labels[metric];
  // }

  private getTooltipRows(bar: any) {
    const rows: any[] = [];

    [this.data1, this.data2, this.data3, this.data4].forEach((series) => {
      if (!series) return;

      const match = series.find((d) => d.start === bar.start);
      if (match) {
        rows.push({
          label: match?.['key'], // dynamic label
          value: match?.['value'], // dynamic value
        });
      }
    });

    return rows;
  }

  getWeeksBetween(startDate: any, endDate: any) {
    const year = dayjs(startDate).year();
    const start = dayjs(startDate).isoWeekday(1);
    const end = dayjs(endDate).isoWeekday(7);

    const weeks: { weekNumber: number }[] = [];

    let current = start.clone();

    while (current.isSame(end) || current.isBefore(end)) {
      weeks.push({
        weekNumber: current.isoWeek(),
        // weekStart: current.clone().startOf('isoWeek').format('YYYY-MM-DD'),
        // weekEnd: current.clone().endOf('isoWeek').format('YYYY-MM-DD'),
      });

      current = current.add(1, 'week');
    }

    return 'week ' + (weeks.length > 0 ? weeks[0].weekNumber : ''); // Added safety check
  }

  private has5OrMoreYearsOfData(): boolean {
    const allData = [
      ...(Array.isArray(this.data1) ? this.data1 : []),
      ...(Array.isArray(this.data2) ? this.data2 : []),
      ...(Array.isArray(this.data3) ? this.data3 : []),
      ...(Array.isArray(this.data4) ? this.data4 : [])
    ];
    if (allData.length === 0) return false;

    const uniqueYears = new Set<number>();
    allData.forEach(d => {
      if (!d) return;
      const yr = this.getYearFromData(d);
      if (yr !== null && !isNaN(yr)) {
        uniqueYears.add(yr);
      }
    });

    return uniqueYears.size >= 5;
  }

  private getYearFromData(d: any): number | null {
    if (!d) return null;

    if (d.start) {
      const yr = dayjs(d.start).year();
      if (!isNaN(yr)) return yr;
    }
    if (d.end) {
      const yr = dayjs(d.end).year();
      if (!isNaN(yr)) return yr;
    }
    if (d.date) {
      const yr = dayjs(d.date).year();
      if (!isNaN(yr)) return yr;
    }

    if (d.label) {
      const labelStr = String(d.label).trim();
      if (/^\d{4}$/.test(labelStr)) {
        const yr = parseInt(labelStr, 10);
        if (!isNaN(yr)) return yr;
      }

      const match = labelStr.match(/\b(20\d{2}|19\d{2})\b/);
      if (match) {
        return parseInt(match[1], 10);
      }

      const parsed = dayjs(labelStr);
      if (parsed.isValid()) {
        return parsed.year();
      }
    }

    return null;
  }
}

